import React, { useState, useEffect, useMemo, useRef } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import apiClient from '../../utils/api';
import { useTranslation } from '../../i18n';

const TYPE_COLORS = {
  person: '#6366f1', company: '#f59e0b', project: '#10b981',
  place: '#3b82f6', concept: '#8b5cf6', event: '#ec4899', unknown: '#6b7280',
};

const MemoryTab = () => {
  const { t } = useTranslation();
  const [provider, setProvider] = useState('s3_vectors');
  const [wikiAvailable, setWikiAvailable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Wiki browser state
  const [wikiData, setWikiData] = useState(null);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [viewMode, setViewMode] = useState('grouped');
  const graphRef = useRef();

  useEffect(() => { loadPreference(); }, []);
  useEffect(() => { if (provider === 'wiki') loadWiki(); }, [provider]);

  const loadPreference = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/memory/preference');
      setProvider(response.data.provider || 's3_vectors');
      setWikiAvailable(response.data.wiki_available || false);
      if (response.data.provider === 'wiki') loadWiki();
    } catch (err) { setError(t('memoryProviderFailed')); }
    finally { setLoading(false); }
  };

  const loadWiki = async () => {
    try {
      const response = await apiClient.get('/memory/wiki');
      setWikiData(response.data);
    } catch (err) { console.error('Error loading wiki:', err); }
  };

  const handleSave = async () => {
    setSaving(true); setSuccess(''); setError('');
    try {
      await apiClient.put('/memory/preference', { provider });
      setSuccess(t('memoryProviderUpdated'));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || t('memoryProviderFailed'));
      setTimeout(() => setError(''), 5000);
    } finally { setSaving(false); }
  };

  const graphData = useMemo(() => {
    if (!wikiData?.entities) return { nodes: [], links: [] };
    const nodes = Object.entries(wikiData.entities).map(([slug, info]) => ({
      id: slug, name: info.name, type: info.type || 'unknown', factCount: info.fact_count || 0,
    }));
    const links = (wikiData.relationships || []).map(r => ({ source: r.source, target: r.target, type: r.type }));
    return { nodes, links };
  }, [wikiData]);

  const groupedEntities = useMemo(() => {
    if (!wikiData?.entities) return {};
    const groups = {};
    Object.entries(wikiData.entities).forEach(([slug, info]) => {
      const type = info.type || 'unknown';
      if (!groups[type]) groups[type] = [];
      groups[type].push({ slug, ...info });
    });
    Object.values(groups).forEach(list => list.sort((a, b) => a.name.localeCompare(b.name)));
    return groups;
  }, [wikiData]);

  const pageContent = wikiData?.pages?.[selectedSlug] || null;

  if (loading) return <div className="tab-content"><div className="tab-header"><h2>{t('memoryBackend')}</h2><p>Loading...</p></div></div>;

  return (
    <div className="tab-content">
      <div className="tab-header">
        <h2>{t('memoryBackend')}</h2>
        <p>{t('memoryStrategyDescription')}</p>
      </div>

      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {/* Compact provider selector — inline row */}
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', padding: '0.5rem 0.75rem', border: provider === 's3_vectors' ? '2px solid #2563eb' : '1px solid #d1d5db', borderRadius: '8px', background: provider === 's3_vectors' ? '#eff6ff' : 'white', flex: '1', minWidth: '200px' }}>
          <input type="radio" name="memory_provider" value="s3_vectors" checked={provider === 's3_vectors'} onChange={() => setProvider('s3_vectors')} />
          <div>
            <div style={{ fontWeight: 600, fontSize: '0.8rem' }}>{t('memoryS3Vectors')}</div>
            <div style={{ fontSize: '0.68rem', color: '#6b7280' }}>{t('memoryS3VectorsDesc')}</div>
          </div>
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: wikiAvailable ? 'pointer' : 'not-allowed', padding: '0.5rem 0.75rem', border: provider === 'wiki' ? '2px solid #2563eb' : '1px solid #d1d5db', borderRadius: '8px', background: provider === 'wiki' ? '#eff6ff' : 'white', opacity: wikiAvailable ? 1 : 0.5, flex: '1', minWidth: '200px' }}>
          <input type="radio" name="memory_provider" value="wiki" checked={provider === 'wiki'} onChange={() => setProvider('wiki')} disabled={!wikiAvailable} />
          <div>
            <div style={{ fontWeight: 600, fontSize: '0.8rem' }}>
              {t('memoryWiki')}
              {!wikiAvailable && <span style={{ marginLeft: '0.4rem', fontSize: '0.65rem', color: '#dc2626' }}>({t('memoryUnavailable')})</span>}
            </div>
            <div style={{ fontSize: '0.68rem', color: '#6b7280' }}>{t('memoryWikiDesc')}</div>
          </div>
        </label>

        <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ padding: '0.5rem 1rem', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
          {saving ? t('saving') : t('savePreferences')}
        </button>
      </div>

      {/* Wiki browser — takes up the rest of the space */}
      {provider === 'wiki' && wikiData && wikiData.entity_count > 0 && (
        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
          {/* Semantic search bar */}
          <SearchBar onResultClick={(slug) => setSelectedSlug(slug)} />
          {/* Toolbar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
              {wikiData.entity_count} {t('memoryEntities')} · {wikiData.relationships?.length || 0} {t('memoryRelationships')}
            </span>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              {['grouped', 'graph'].map(v => (
                <button key={v} onClick={() => { setViewMode(v); setSelectedSlug(null); }}
                  style={{ padding: '0.25rem 0.5rem', border: 'none', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', background: viewMode === v ? '#2563eb' : '#f3f4f6', color: viewMode === v ? 'white' : '#374151' }}>
                  {v === 'grouped' ? `🏷️ ${t('memoryViewList')}` : `🔗 ${t('memoryViewGraph')}`}
                </button>
              ))}
            </div>
          </div>

          {/* Grouped view */}
          {viewMode === 'grouped' && (
            <div style={{ display: 'grid', gridTemplateColumns: selectedSlug ? '220px 1fr' : '1fr 1fr 1fr', gap: '0.75rem' }}>
              {selectedSlug ? (
                <>
                  <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
                    {Object.entries(groupedEntities).map(([type, entities]) => (
                      <div key={type} style={{ marginBottom: '0.5rem' }}>
                        <div style={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', color: TYPE_COLORS[type] || '#6b7280', marginBottom: '0.2rem' }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: TYPE_COLORS[type] || '#6b7280', display: 'inline-block', marginRight: 3 }} />
                          {type}
                        </div>
                        {entities.map(e => (
                          <div key={e.slug} onClick={() => setSelectedSlug(e.slug)}
                            style={{ padding: '0.3rem 0.4rem', marginBottom: '0.15rem', cursor: 'pointer', borderRadius: '3px', fontSize: '0.72rem', background: selectedSlug === e.slug ? '#eff6ff' : 'transparent', fontWeight: selectedSlug === e.slug ? 600 : 400, color: selectedSlug === e.slug ? '#1e40af' : '#374151' }}>
                            {e.name}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                  <PagePanel slug={selectedSlug} content={pageContent} onClose={() => setSelectedSlug(null)} onNavigate={setSelectedSlug} />
                </>
              ) : (
                Object.entries(groupedEntities).map(([type, entities]) => (
                  <div key={type}>
                    <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', color: TYPE_COLORS[type] || '#6b7280', marginBottom: '0.4rem', borderBottom: `2px solid ${TYPE_COLORS[type] || '#e5e7eb'}`, paddingBottom: '0.25rem' }}>
                      {type} ({entities.length})
                    </div>
                    {entities.map(e => (
                      <div key={e.slug} onClick={() => setSelectedSlug(e.slug)}
                        style={{ padding: '0.35rem 0.4rem', marginBottom: '0.2rem', cursor: 'pointer', borderRadius: '4px', fontSize: '0.75rem', border: '1px solid #e5e7eb', background: 'white' }}>
                        <span style={{ fontWeight: 500 }}>{e.name}</span>
                        {e.fact_count > 0 && <span style={{ fontSize: '0.6rem', color: '#9ca3af', marginLeft: '0.4rem' }}>{e.fact_count}</span>}
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Graph view */}
          {viewMode === 'graph' && (
            <div style={{ display: 'grid', gridTemplateColumns: selectedSlug ? '1fr 280px' : '1fr', gap: '0.75rem' }}>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', background: '#1a1a2e', height: '420px', position: 'relative' }}>
                <ForceGraph2D ref={graphRef} graphData={graphData} nodeLabel={n => `${n.name} (${n.type})`} nodeRelSize={5}
                  linkColor={() => '#4b5563'} linkDirectionalArrowLength={4} linkDirectionalArrowRelPos={1} linkWidth={1.5} linkLabel={l => l.type}
                  onNodeClick={(node) => setSelectedSlug(node.id)} onBackgroundClick={() => setSelectedSlug(null)}
                  nodeCanvasObject={(node, ctx, globalScale) => {
                    const isSelected = node.id === selectedSlug;
                    const radius = isSelected ? 6 : 3 + Math.min(node.factCount, 4);
                    const color = TYPE_COLORS[node.type] || '#6b7280';
                    if (isSelected) { ctx.beginPath(); ctx.arc(node.x, node.y, radius + 3, 0, 2 * Math.PI); ctx.fillStyle = `${color}33`; ctx.fill(); }
                    ctx.beginPath(); ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI); ctx.fillStyle = color; ctx.fill();
                    const fontSize = Math.max(9 / globalScale, 2.5);
                    ctx.font = `${isSelected ? 'bold ' : ''}${fontSize}px Sans-Serif`; ctx.fillStyle = '#e5e7eb'; ctx.textAlign = 'center'; ctx.textBaseline = 'top';
                    ctx.fillText(node.name, node.x, node.y + radius + 2);
                  }}
                  height={420} backgroundColor="#1a1a2e" cooldownTicks={80}
                />
                {/* Legend */}
                <div style={{ position: 'absolute', bottom: 6, left: 6, display: 'flex', gap: '0.4rem', flexWrap: 'wrap', background: 'rgba(26,26,46,0.9)', padding: '0.2rem 0.4rem', borderRadius: '3px' }}>
                  {Object.entries(TYPE_COLORS).filter(([t]) => groupedEntities[t]).map(([type, color]) => (
                    <span key={type} style={{ fontSize: '0.55rem', color, fontWeight: 600 }}>● {type}</span>
                  ))}
                </div>
              </div>
              {selectedSlug && pageContent && <PagePanel slug={selectedSlug} content={pageContent} onClose={() => setSelectedSlug(null)} onNavigate={setSelectedSlug} />}
            </div>
          )}
        </div>
      )}

      {provider === 'wiki' && wikiData && wikiData.entity_count === 0 && (
        <div style={{ textAlign: 'center', color: '#9ca3af', padding: '2rem', borderTop: '1px solid #e5e7eb', marginTop: '1rem' }}>
          {t('memoryEmpty')}
        </div>
      )}
    </div>
  );
};

/**
 * Graph search over the wiki. The API returns the matched facts plus the pages
 * and one-hop neighbours behind them, so each row links back to its page.
 */
const SearchBar = ({ onResultClick }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [result, setResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  const handleSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    setSearchError('');
    try {
      const response = await apiClient.get(`/memory/wiki/search?q=${encodeURIComponent(query)}&limit=5`);
      setResult(response.data);
    } catch (err) {
      console.error('Search failed:', err);
      setSearchError(t('memorySearchFailed'));
      setResult(null);
    } finally {
      setSearching(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const clear = () => { setQuery(''); setResult(null); setSearchError(''); };

  const matches = result?.matches || [];
  const related = (result?.entities || []).filter(e => e.hop === 1 && e.summary);
  const hasSearched = result !== null;

  return (
    <div style={{ marginBottom: '1rem' }}>
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: hasSearched || searchError ? '0.5rem' : 0 }}>
        <input
          type="text" value={query} onChange={e => setQuery(e.target.value)} onKeyDown={handleKeyDown}
          placeholder={t('memorySearchPlaceholder')} aria-label={t('memorySearchPlaceholder')}
          style={{ flex: 1, padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.8rem' }}
        />
        <button onClick={handleSearch} disabled={searching || !query.trim()}
          style={{ padding: '0.5rem 0.75rem', background: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', opacity: searching || !query.trim() ? 0.6 : 1 }}>
          {searching ? '...' : '🔍'}
        </button>
        {hasSearched && (
          <button onClick={clear}
            style={{ padding: '0.5rem 0.75rem', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.75rem', cursor: 'pointer' }}>
            {t('memorySearchClear')}
          </button>
        )}
      </div>

      {searchError && <div style={{ fontSize: '0.72rem', color: '#dc2626' }}>{searchError}</div>}

      {hasSearched && !searchError && matches.length === 0 && (
        <div style={{ fontSize: '0.72rem', color: '#9ca3af', padding: '0.4rem' }}>{t('memorySearchNoResults')}</div>
      )}

      {matches.length > 0 && (
        <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '0.5rem' }}>
          {matches.map((m, i) => (
            <div key={`${m.slug}-${i}`} onClick={() => m.slug && onResultClick(m.slug)}
              role="button" tabIndex={0}
              onKeyDown={e => { if (e.key === 'Enter' && m.slug) onResultClick(m.slug); }}
              style={{ padding: '0.4rem 0.5rem', marginBottom: '0.25rem', cursor: m.slug ? 'pointer' : 'default', borderRadius: '4px', fontSize: '0.75rem', background: 'white', border: '1px solid #e5e7eb', borderLeft: `3px solid ${TYPE_COLORS[m.entity_type] || '#6b7280'}` }}>
              <div style={{ color: '#111827', lineHeight: 1.4 }}>{m.text}</div>
              <div style={{ marginTop: '0.2rem', display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600, color: TYPE_COLORS[m.entity_type] || '#2563eb', fontSize: '0.68rem' }}>{m.entity_name}</span>
                <span style={{ color: '#9ca3af', fontSize: '0.62rem' }}>{m.entity_type}</span>
                <span style={{ color: '#9ca3af', fontSize: '0.62rem' }}>
                  · {m.source === 'fact_match'
                    ? `${t('memorySearchMatchedFact')}${typeof m.score === 'number' ? ` ${m.score}` : ''}`
                    : t('memorySearchFromPage')}
                </span>
              </div>
            </div>
          ))}

          {related.length > 0 && (
            <div style={{ marginTop: '0.4rem', paddingTop: '0.4rem', borderTop: '1px dashed #e5e7eb' }}>
              <div style={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', color: '#9ca3af', marginBottom: '0.25rem' }}>
                {t('memorySearchRelated')}
              </div>
              <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                {related.map(e => (
                  <span key={e.slug} onClick={() => onResultClick(e.slug)} title={e.summary}
                    role="button" tabIndex={0}
                    onKeyDown={ev => { if (ev.key === 'Enter') onResultClick(e.slug); }}
                    style={{ padding: '0.15rem 0.4rem', borderRadius: '10px', fontSize: '0.65rem', cursor: 'pointer', background: `${TYPE_COLORS[e.entity_type] || '#6b7280'}1a`, color: TYPE_COLORS[e.entity_type] || '#374151', fontWeight: 600 }}>
                    {e.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const PagePanel = ({ slug, content, onClose, onNavigate }) => {
  const renderContent = (text) => {
    const parts = text.split(/(\[\[[^\]]+\]\])/g);
    return parts.map((part, i) => {
      const match = part.match(/^\[\[([^\]]+)\]\]$/);
      if (!match) return <span key={i}>{part}</span>;
      // Links are written as [[type/slug]]; pages are keyed by the bare slug.
      const target = match[1].includes('/') ? match[1].split('/').pop() : match[1];
      return (
        <span key={i} onClick={() => onNavigate(target)} role="button" tabIndex={0}
          onKeyDown={e => { if (e.key === 'Enter') onNavigate(target); }}
          style={{ color: '#2563eb', cursor: 'pointer', fontWeight: 500, textDecoration: 'underline' }}>
          {match[1]}
        </span>
      );
    });
  };
  return (
    <div style={{ padding: '0.75rem', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', maxHeight: '420px', overflowY: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <h4 style={{ margin: 0, fontSize: '0.78rem', fontFamily: 'monospace' }}>{slug}.md</h4>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem' }}>✕</button>
      </div>
      <pre style={{ fontSize: '0.7rem', lineHeight: '1.6', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace', margin: 0, color: '#374151' }}>
        {(content || '').split('\n').map((line, i) => <div key={i}>{renderContent(line)}</div>)}
      </pre>
    </div>
  );
};

export default MemoryTab;
